import { useMemo, useCallback, useState, useEffect, useRef } from 'react';

import useLoadSpeakers from '@/connection/hooks/use-load-speakers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectSessionByUuid } from '@/store/slices/project/selectors';
import { getSpeakers } from '@/store/slices/speakers/selectors';
import { Speaker } from '@/types/speakers.types';
import CustomSelect from '@ui/select/custom-select/custom-select';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import useDefaultSpeakers from './helpers/use-default-speakers';
import RenderUnfocusedLabelContent from './components/render-unfocused-label-content';
import RenderFocusedLabelContent from './components/render-focused-label-content';
import RenderMenuOptionContent from './components/render-menu-option-content';
import RenderNoOptionsContent from './components/render-no-options-content';
import saveUpdatedCast from '../helpers/save-updated-cast';
import InviteCastModal from './components/invite-cast-modal/invite-cast-modal';
import RenderHeaderLabelContent from './components/render-header-label-content';
import { setNewSpeaker } from '@/store/slices/speakers';

const CastAndCrewSelectWithInvites = ({ sessionUuid }: { sessionUuid: string }) => {
  useLoadSpeakers({ skipIfSpeakersExist: true });

  const dispatch = useAppDispatch();

  const speakers = useAppSelector(getSpeakers);
  const session = useAppSelector(selectSessionByUuid(sessionUuid));

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [lockActiveState, setLockActiveState] = useState(false);
  const [initialName, setInitialName] = useState('');

  // this timer just lets us keep the select component "active state" active while the popover is closing
  // otherwise we'll end up triggering the onFocusClosed handler
  const resetActiveStateTimer = useRef<NodeJS.Timeout>(null);
  useEffect(() => {
    if (!showInviteModal && lockActiveState) {
      if (resetActiveStateTimer.current) clearTimeout(resetActiveStateTimer.current);
      resetActiveStateTimer.current = setTimeout(() => {
        setLockActiveState(false);
      }, 1500);
    }

    return () => {
      if (resetActiveStateTimer.current) clearTimeout(resetActiveStateTimer.current);
    };
  }, [lockActiveState, showInviteModal]);

  const defaultOptions = useDefaultSpeakers({ session: session || null });

  const options = useMemo(() => {
    return (speakers || []).reduce(
      (accumulatedOptions: TReactSelectOption<string, number, Speaker>[], option: Speaker) => {
        const label = `${option.first_name} ${option.last_name} ${option?.email || ''}`.trim();
        accumulatedOptions.push({
          label,
          value: option.speaker,
          meta: option,
        });
        return accumulatedOptions;
      },
      []
    );
  }, [speakers]);

  const onFocusClosed = useCallback(
    async (selectedOptions: TReactSelectOption[]) => {
      await saveUpdatedCast({
        session,
        defaultOptions,
        selectedOptions,
        dispatch,
      });
    },
    [defaultOptions, dispatch, session]
  );

  const handleNewCast = (speaker: Speaker) => {
    onFocusClosed([
      ...defaultOptions,
      {
        label: `${speaker.first_name} ${speaker.last_name} ${speaker?.email || ''}`.trim(),
        value: speaker.speaker,
        meta: speaker,
      },
    ]);
    dispatch(setNewSpeaker(speaker));
    setShowInviteModal(false);
  };

  return (
    <>
      <CustomSelect
        lockActiveState={lockActiveState}
        onAddNewSelect={(inputValue) => {
          setShowInviteModal(true);
          setLockActiveState(true);
          setInitialName(inputValue);
        }}
        id="cast-and-crew-select-with-invites"
        placeholder="Add members"
        options={options}
        controlledOptions={defaultOptions}
        onFocusClosed={onFocusClosed}
        RenderHeaderLabelContent={RenderHeaderLabelContent}
        RenderUnfocusedLabelContent={RenderUnfocusedLabelContent}
        RenderFocusedLabelContent={RenderFocusedLabelContent}
        RenderMenuOptionContent={RenderMenuOptionContent}
        RenderNoOptionsContent={RenderNoOptionsContent}
      />

      <InviteCastModal
        open={showInviteModal}
        initialName={initialName}
        onClose={() => setShowInviteModal(false)}
        handleNewCast={handleNewCast}
      />
    </>
  );
};

export default CastAndCrewSelectWithInvites;
